import { useQuery } from "convex/react";
import { anyApi } from "convex/server";

interface LeaseDoc {
  _id: string;
  filename: string;
  uploadedAt: number;
  url: string | null;
}

// Tenant-facing section showing the documents on their CURRENT
// lease only. When the lease ends and a new one starts, this section
// resets — the new tenants on the new lease never see these.
export function MyLeaseSection() {
  const docs = useQuery(anyApi.leases.myCurrentLeaseDocs, {}) as
    | LeaseDoc[]
    | undefined;

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="px-6 pt-5 pb-4 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-900">Lease</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Documents for your current rental agreement.
        </p>
      </header>
      <Body docs={docs} />
    </section>
  );
}

function Body({ docs }: { docs: LeaseDoc[] | undefined }) {
  if (docs === undefined) {
    return <p className="px-6 py-4 text-sm text-slate-500">Loading…</p>;
  }
  if (docs.length === 0) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm font-medium text-slate-900">
          No lease documents yet.
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Your landlord will upload your signed agreement here.
        </p>
      </div>
    );
  }
  return (
    <ul className="px-6 py-4 divide-y divide-slate-100">
      {docs.map((d) => (
        <li
          key={d._id}
          className="py-3 flex items-center justify-between gap-3 first:pt-0"
        >
          <div className="min-w-0 flex-1">
            {d.url ? (
              <a
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-slate-900 hover:underline truncate block"
              >
                {d.filename}
              </a>
            ) : (
              <span className="text-sm font-medium text-slate-500 truncate block">
                {d.filename}
              </span>
            )}
            <p className="text-xs text-slate-500">
              Uploaded {formatTimestamp(d.uploadedAt)}
            </p>
          </div>
          {d.url && (
            <a
              href={d.url}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              View
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
